/**
 * @sazeo/sdk — Payroll Tax Integration
 *
 * Hooks for Georgian payroll tax calculations, configurations, and report generation.
 * All requests carry the caller's JWT so RLS enforces tenant + role isolation.
 */

import { type TenantSupabaseClient, getTenantClient } from '@sazeo/supabase-tenant';
import { useCallback, useState } from 'react';

// ===================================================================
// Types
// ===================================================================

export interface TaxConfiguration {
  id: string;
  tenant_id: string;
  tax_year: number;
  pit_rate: number;
  health_contribution_employee: number;
  pension_contribution_employee: number;
  health_contribution_employer: number;
  pension_contribution_employer: number;
  annual_deduction_lari: number;
  tax_free_income_limit: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaxCalculationInput {
  gross_salary: number;
  bonuses?: number;
  deductions?: number;
  employee_id: string;
  payroll_month: string; // YYYY-MM-DD
}

export interface TaxCalculationOutput {
  gross_salary: number;
  pit_withheld: number;
  health_contribution_employee: number;
  pension_contribution_employee: number;
  health_contribution_employer: number;
  pension_contribution_employer: number;
  net_pay_to_employee: number;
  tax_calculation_details: {
    taxable_income: number;
    applied_deduction: number;
    effective_rate: number;
  };
}

export interface PayrollTaxWithholding {
  id: string;
  tenant_id: string;
  payroll_run_id: string | null;
  employee_id: string;
  gross_salary: number;
  pit_withheld: number;
  health_contribution_employee: number;
  pension_contribution_employee: number;
  health_contribution_employer: number;
  pension_contribution_employer: number;
  bonuses: number;
  deductions: number;
  net_pay_to_employee: number;
  tax_calculation_method: string;
  payroll_month: string;
  annual_deduction_applied: number;
  taxable_income: number;
  created_at: string;
}

export type TaxReportType = 'monthly' | 'annual' | 'ecls' | 'emis';
export type TaxReportStatus = 'draft' | 'submitted' | 'confirmed' | 'filed';

export interface TaxReport {
  id: string;
  tenant_id: string;
  report_type: TaxReportType;
  tax_year: number;
  tax_month: number | null;
  status: TaxReportStatus;
  total_employees: number | null;
  total_gross_salary: number | null;
  total_pit_withheld: number | null;
  total_health_contributions: number | null;
  total_pension_contributions: number | null;
  generated_at: string;
  submitted_at: string | null;
  submitted_by: string | null;
  file_content: string | null;
  file_format: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// ===================================================================
// Helpers
// ===================================================================

interface SupabaseAuthLike {
  auth: {
    getSession: () => Promise<{ data: { session: { access_token: string } | null } }>;
  };
}

function resolveClient(supabase: unknown): SupabaseAuthLike {
  if (supabase) return supabase as SupabaseAuthLike;
  try {
    return getTenantClient() as unknown as TenantSupabaseClient as unknown as SupabaseAuthLike;
  } catch {
    return { auth: { getSession: async () => ({ data: { session: null } }) } };
  }
}

async function getAuthHeaders(supabase: unknown): Promise<Record<string, string>> {
  const client = resolveClient(supabase);
  const { data } = await client.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function apiFetch<T>(supabase: unknown, path: string, init?: RequestInit): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({ error: { message: res.statusText } }))) as {
      error?: { message?: string };
    };
    throw new Error(body.error?.message ?? res.statusText);
  }
  if (res.status === 204) return {} as T;
  return res.json() as Promise<T>;
}

// ===================================================================
// Hooks
// ===================================================================

export function useTaxConfiguration(supabase: unknown, tenantId: string, taxYear: number) {
  const [data, setData] = useState<TaxConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(() => {
    if (!tenantId || !taxYear) return;
    setIsLoading(true);
    setError(null);
    apiFetch<TaxConfiguration>(
      supabase,
      `addons/payroll/tax-config?tenant_id=${tenantId}&tax_year=${taxYear}`,
    )
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => setIsLoading(false));
  }, [supabase, tenantId, taxYear]);

  // Fetch on mount
  React.useEffect(() => {
    fetch();
  }, [fetch]);

  const refetch = useCallback(() => fetch(), [fetch]);

  return { data, isLoading, error, refetch };
}

export function useUpsertTaxConfiguration(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      taxYear: number;
      pit_rate?: number;
      health_contribution_employee?: number;
      pension_contribution_employee?: number;
      health_contribution_employer?: number;
      pension_contribution_employer?: number;
      annual_deduction_lari?: number;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<TaxConfiguration>(supabase, 'addons/payroll/tax-config', {
          method: 'PUT',
          body: JSON.stringify({
            tenant_id: input.tenantId,
            tax_year: input.taxYear,
            pit_rate: input.pit_rate,
            health_contribution_employee: input.health_contribution_employee,
            pension_contribution_employee: input.pension_contribution_employee,
            health_contribution_employer: input.health_contribution_employer,
            pension_contribution_employer: input.pension_contribution_employer,
            annual_deduction_lari: input.annual_deduction_lari,
          }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useCalculatePayrollTax(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: { tenantId: string } & TaxCalculationInput) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<TaxCalculationOutput>(supabase, 'addons/payroll/tax-calculate', {
          method: 'POST',
          body: JSON.stringify({
            tenant_id: input.tenantId,
            gross_salary: input.gross_salary,
            bonuses: input.bonuses ?? 0,
            deductions: input.deductions ?? 0,
            employee_id: input.employee_id,
            payroll_month: input.payroll_month,
          }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useTaxWithholdings(
  supabase: unknown,
  tenantId: string,
  filters?: { payroll_month?: string; employee_id?: string },
) {
  const [data, setData] = useState<PayrollTaxWithholding[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(() => {
    if (!tenantId) return;
    const params = new URLSearchParams({ tenant_id: tenantId });
    if (filters?.payroll_month) params.set('payroll_month', filters.payroll_month);
    if (filters?.employee_id) params.set('employee_id', filters.employee_id);

    setIsLoading(true);
    setError(null);
    apiFetch<PayrollTaxWithholding[]>(supabase, `addons/payroll/tax-withholdings?${params}`)
      .then((d) => setData(d))
      .catch((e) => setError(e instanceof Error ? e : new Error(String(e))))
      .finally(() => setIsLoading(false));
  }, [supabase, tenantId, filters?.payroll_month, filters?.employee_id]);

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, isLoading, error, refetch: fetch };
}

export function useGenerateTaxReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (input: {
      tenantId: string;
      reportType: TaxReportType;
      taxYear: number;
      taxMonth?: number;
    }) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<TaxReport>(supabase, 'addons/payroll/tax-report/generate', {
          method: 'POST',
          body: JSON.stringify({
            tenant_id: input.tenantId,
            report_type: input.reportType,
            tax_year: input.taxYear,
            tax_month: input.taxMonth,
          }),
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

export function useDownloadTaxReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const download = useCallback(
    async (reportId: string) => {
      setIsPending(true);
      setError(null);
      try {
        const res = await fetch(`/functions/v1/addons/payroll/tax-report/download/${reportId}`, {
          method: 'GET',
          headers: { ...(await getAuthHeaders(supabase)) },
        });
        if (!res.ok) throw new Error(res.statusText);
        return res;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { download, isPending, error };
}

export function useSubmitTaxReport(supabase: unknown) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback(
    async (reportId: string) => {
      setIsPending(true);
      setError(null);
      try {
        return await apiFetch<TaxReport>(supabase, `addons/payroll/tax-report/${reportId}/submit`, {
          method: 'POST',
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setIsPending(false);
      }
    },
    [supabase],
  );

  return { mutate, isPending, error };
}

// ===================================================================
// Client-side calculation (preview only, not for final payroll)
// ===================================================================

export function calculateTaxPreview(input: {
  gross_salary: number;
  annual_deduction: number;
  pit_rate?: number;
  health_emp?: number;
  pension_emp?: number;
  health_empr?: number;
  pension_empr?: number;
  bonuses?: number;
  deductions?: number;
}): TaxCalculationOutput {
  const monthlyDeduction = input.annual_deduction / 12;
  const taxableIncome = Math.max(0, input.gross_salary - monthlyDeduction);
  const pit = Math.round(((taxableIncome * (input.pit_rate ?? 20)) / 100) * 100) / 100;
  const healthEmp = Math.round(((input.gross_salary * (input.health_emp ?? 2)) / 100) * 100) / 100;
  const pensionEmp =
    Math.round(((input.gross_salary * (input.pension_emp ?? 2)) / 100) * 100) / 100;
  const healthEmpr =
    Math.round(((input.gross_salary * (input.health_empr ?? 2.86)) / 100) * 100) / 100;
  const pensionEmpr =
    Math.round(((input.gross_salary * (input.pension_empr ?? 2)) / 100) * 100) / 100;
  const netPay =
    Math.round(
      (input.gross_salary -
        pit -
        healthEmp -
        pensionEmp +
        (input.bonuses ?? 0) -
        (input.deductions ?? 0)) *
        100,
    ) / 100;

  return {
    gross_salary: input.gross_salary,
    pit_withheld: pit,
    health_contribution_employee: healthEmp,
    pension_contribution_employee: pensionEmp,
    health_contribution_employer: healthEmpr,
    pension_contribution_employer: pensionEmpr,
    net_pay_to_employee: netPay,
    tax_calculation_details: {
      taxable_income: taxableIncome,
      applied_deduction: monthlyDeduction,
      effective_rate: taxableIncome > 0 ? Math.round((pit / taxableIncome) * 10000) / 100 : 0,
    },
  };
}

// Re-export React for useEffect in hooks
import React from 'react';
